import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  Mail,
  MessageCircle,
  Megaphone,
  Check,
} from "lucide-react";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000/api";

interface PreferencesData {
  customerName: string;
  businessNames: string[];
  transactionalEmailOptOut: boolean;
  transactionalWhatsAppOptOut: boolean;
  marketingConsent: boolean;
  // Baja explícita de campañas comerciales (RGPD Art.21 / LSSI 21.2). El PUT
  // de esta página mantiene este campo en espejo inverso de marketingConsent,
  // pero puede llegar divergente de origen (backfills de migraciones, alta
  // de consentimiento al reservar, importación CSV): por eso el estado real
  // del toggle SIEMPRE debe combinar los dos campos, nunca solo
  // marketingConsent.
  marketingEmailOptOut?: boolean;
}

export default function Preferences() {
  const { token } = useParams<{ token: string }>();
  // `c` = id de la campaña de origen del enlace (query param opaco, ver
  // send-service.ts). Se reenvía al backend en la baja para atribuirla a esa
  // campaña; si no viene, la baja funciona igual pero sin atribución.
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get("c");
  const campaignQs = campaignId ? `?c=${encodeURIComponent(campaignId)}` : "";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<PreferencesData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/public/preferences/${token}`);
        const json = await res.json();
        if (!res.ok) {
          setError(json.message || "Enlace no válido");
        } else {
          setData(json.data);
        }
      } catch {
        setError("Error de conexión");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const update = async (
    field:
      | "transactionalEmailOptOut"
      | "transactionalWhatsAppOptOut"
      | "marketingConsent",
    value: boolean,
  ) => {
    if (!data) return;
    setSaving(true);
    setError(null);
    const next = { ...data, [field]: value };
    if (field === "marketingConsent") {
      // El backend guarda marketingEmailOptOut en espejo inverso al recibir
      // este campo (ver preferences-controller.ts) — se refleja también aquí
      // para que el toggle no necesite un refresco para verse correcto tras
      // reactivarse desde un estado previamente divergente.
      next.marketingEmailOptOut = !value;
    }
    setData(next);
    try {
      const res = await fetch(`${API_URL}/public/preferences/${token}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        // `c` viaja en el body (el backend lo lee de query o body indistintamente);
        // el backend solo lo usa cuando la actualización es una baja de marketing.
        body: JSON.stringify({
          [field]: value,
          ...(campaignId ? { c: campaignId } : {}),
        }),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        setError(json.message || "Error al guardar");
        // revert
        setData(data);
      } else {
        setSavedAt(Date.now());
      }
    } catch {
      setError("Error de conexión");
      setData(data);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-cyan-50 p-4">
        <Loader2 className="w-10 h-10 animate-spin text-teal-600" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-pink-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-14 h-14 text-rose-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Enlace no válido
          </h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const showSaved = savedAt && Date.now() - savedAt < 3000;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 py-10 px-4">
      <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-6 py-8 text-white">
          <h1 className="text-2xl font-bold">Mis preferencias</h1>
          <p className="text-teal-50 mt-1 text-sm">
            Hola {data.customerName}, gestiona aquí qué comunicaciones quieres
            recibir.
          </p>
        </div>

        <div className="p-6 space-y-6">
          {data.businessNames.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
              <p className="font-medium text-gray-700 mb-1">
                Estos cambios afectan a:
              </p>
              <p>{data.businessNames.join(", ")}</p>
            </div>
          )}

          {error && (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <ToggleRow
            icon={<Mail className="w-5 h-5 text-teal-600" />}
            title="Emails de confirmación y recordatorios"
            description="Confirmaciones de reserva o cita, recordatorios y modificaciones."
            enabled={!data.transactionalEmailOptOut}
            onChange={(v) => update("transactionalEmailOptOut", !v)}
            disabled={saving}
          />

          <ToggleRow
            icon={<MessageCircle className="w-5 h-5 text-teal-600" />}
            title="WhatsApp de confirmación y recordatorios"
            description="Mensajes automáticos de WhatsApp con tus reservas o citas."
            enabled={!data.transactionalWhatsAppOptOut}
            onChange={(v) => update("transactionalWhatsAppOptOut", !v)}
            disabled={saving}
          />

          <ToggleRow
            icon={<Megaphone className="w-5 h-5 text-teal-600" />}
            title="Comunicaciones comerciales"
            description={
              data.marketingConsent && data.marketingEmailOptOut
                ? "Promociones, novedades y campañas de marketing. Actualmente estás dado de baja de nuestras campañas de email: actívalo para volver a recibirlas."
                : "Promociones, novedades y campañas de marketing. Si lo desactivas, te damos de baja de nuestras campañas de email."
            }
            enabled={data.marketingConsent && !data.marketingEmailOptOut}
            onChange={(v) => update("marketingConsent", v)}
            disabled={saving}
          />

          {showSaved && (
            <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium">
              <Check className="w-4 h-4" />
              Cambios guardados
            </div>
          )}

          <div className="border-t border-gray-200 pt-4 text-xs text-gray-500">
            <p>
              Tus reservas y citas activas no se cancelan al desactivar las
              comunicaciones. Solo dejarás de recibir avisos automáticos.
            </p>
          </div>

          <div className="text-center">
            <Link
              to={`/baja/${token}${campaignQs}`}
              className="text-sm text-rose-600 hover:text-rose-700 hover:underline"
            >
              Darme de baja de todo
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({
  icon,
  title,
  description,
  enabled,
  onChange,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  enabled: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50">
      <div className="flex-shrink-0 mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 text-sm">{title}</p>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!enabled)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
          enabled ? "bg-teal-600" : "bg-gray-300"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        aria-pressed={enabled}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );
}
